"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { MapPin, ArrowRight, Maximize2, Calendar, Ruler, BrickWall } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const PROJECTS = [{
  id: "p1",
  title: "Pacific Heights Driveway",
  category: "Residential",
  location: "\n",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealing_residential_driveway.png",
  description: "A comprehensive sealing treatment for a high-traffic residential driveway. We applied a deep-penetrating silane-siloxane sealer to provide maximum protection against water ingress and oil staining while enhancing the natural aggregate color.",
  specs: {
    area: "Exposed Aggregate",
    year: "2023",
    architect: "Residential Client"
  },
  materials: ["HydroGuard Pro-Series", "Oil-Resistant Finish"],
  heightClass: "h-96"
}, {
  id: "p2",
  title: "Metro Plaza Foundation",
  category: "Commercial",
  location: "\n",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealing_commercial_foundation.png",
  description: "Critical waterproofing and structural sealing for a multi-level commercial foundation. Our team utilized advanced crystalline technology to ensure the concrete matrix remains impenetrable to groundwater pressure.",
  specs: {
    area: "Reinforced Concrete",
    year: "2022",
    architect: "Global Builders Corp"
  },
  materials: ["DeepShield Crystalline", "Waterproofing Membrane"],
  heightClass: "h-[500px]"
}, {
  id: "p3",
  title: "Eco-Logistics Warehouse",
  category: "Industrial",
  location: "\n",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealing_industrial_floor.png",
  description: "Industrial-grade floor sealing for a massive logistics hub. The treatment involved heavy-duty densifiers and chemical-resistant sealants designed to withstand constant forklift traffic and potential hazardous spills.",
  specs: {
    area: "Polished Concrete",
    year: "2024",
    architect: "Logistics Solutions Inc"
  },
  materials: ["ArmorPoxy Industrial", "Lithium Densifier"],
  heightClass: "h-80"
}, {
  id: "p4",
  title: "Azure Coast Pool Deck",
  category: "Residential",
  location: "\n",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealing_residential_pool.png",
  description: "Specialized salt-block sealing for a luxury resort pool area. The treatment protects the porous natural stone from saltwater erosion and chlorine damage while maintaining a safe, slip-resistant surface for guests.",
  specs: {
    area: "Natural Coral Stone",
    year: "2023",
    architect: "Island Resorts Group"
  },
  materials: ["SaltBlock Nano-Sealer", "UV-Resistant Coating"],
  heightClass: "h-64"
}, {
  id: "p5",
  title: "Heritage Walkway Sealing",
  category: "Commercial",
  location: "\n",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealing_commercial_brick.png",
  description: "Restorative sealing for a historic commercial walkway. We used a breathable, matte-finish sealant that protects the antique terracotta bricks from moisture damage without trapping vapor or altering the historic aesthetic.",
  specs: {
    area: "Heritage Terracotta",
    year: "2023",
    architect: "Vigan Heritage Council"
  },
  materials: ["Breathable Masonry Saver", "Efflorescence Blocker"],
  heightClass: "h-[400px]"
}, {
  id: "p6",
  title: "Summit Manufacturing Hub",
  category: "Industrial",
  location: "\n",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570251/img/sealing_industrial_hub.png",
  description: "High-performance chemical containment sealing for a manufacturing facility. This project required a multi-layer epoxy and urethane system to protect the substrate from aggressive industrial cleaning agents.",
  specs: {
    area: "Heavy-Duty Concrete Slab",
    year: "2024",
    architect: "Summit Industrial"
  },
  materials: ["OilRepel Ultra-Shield", "Chemical Guard 500"],
  heightClass: "h-96"
}];
export default function OurBuiltLegacy() {
  const [filter, setFilter] = useState("All");
  const filteredProjects = filter === "All" ? PROJECTS : PROJECTS.filter(p => p.category === filter);
  return <section data-section-id="36371" id="recent-work" className="bg-background py-24 sm:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-16 flex flex-col items-center text-center">
          <h2 className="font-sans text-4xl font-bold tracking-tight text-foreground sm:text-5xl uppercase">
            Our Sealed Portfolio
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Discover how Island Seals preserves and protects concrete and stone surfaces 
            across the Philippines with high-performance waterproofing and sealing solutions.
          </p>

          {}
          <div className="mt-10">
            <ToggleGroup type="single" value={filter} onValueChange={value => {
            if (value) setFilter(value);
          }} className="flex flex-wrap justify-center gap-2">
              {["All", "Residential", "Commercial", "Industrial"].map((category, i) => <ToggleGroupItem key={category} value={category} data-index={i} className="rounded-md border border-border px-6 py-2 text-sm font-bold uppercase tracking-wider transition-all hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                    {category}
                  </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        {}
        <div className="columns-1 gap-6 space-y-6 md:columns-2 lg:columns-3">
          {filteredProjects.map((project, i) => <div key={project.id} className="break-inside-avoid" data-index={i}>
              <Dialog>
                <DialogTrigger asChild>
                  <div className="group relative cursor-pointer overflow-hidden rounded-md bg-muted shadow-sm transition-all hover:shadow-xl hover:-translate-y-1">
                    {}
                    <div className={cn("relative w-full overflow-hidden", project.heightClass)}>
                      <img src={project.image} alt={project.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      
                      {}
                      <div className="absolute inset-0 bg-primary/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      
                      {}
                      <div className="absolute bottom-0 left-0 w-full p-6 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <div className="flex items-center gap-2 text-white">
                          <MapPin className="h-4 w-4" />
                          <span className="text-sm font-bold tracking-widest uppercase">
                            {project.location}
                          </span>
                        </div>
                        <h3 className="mt-1 font-sans text-xl font-bold text-white uppercase tracking-tight">
                          {project.title}
                        </h3>
                        <div className="mt-3 flex items-center gap-2 text-xs font-bold text-white/90">
                          <span className="uppercase tracking-widest">View Details</span>
                          <ArrowRight className="h-3 w-3" />
                        </div>
                      </div>

                      {}
                      <div className="absolute top-4 right-4 rounded-sm bg-background/95 px-3 py-1 text-xs font-bold text-foreground uppercase tracking-wider backdrop-blur-md border border-border">
                        {project.category}
                      </div>
                    </div>
                  </div>
                </DialogTrigger>

                {}
                <DialogContent className="max-w-4xl overflow-hidden p-0 sm:max-w-5xl rounded-md border-border">
                  <div className="grid gap-0 md:grid-cols-2">
                    {}
                    <div className="relative h-64 w-full bg-muted md:h-full">
                      <img src={project.image} alt={project.title} className="h-full w-full object-cover" />
                      <div className="absolute top-4 left-4">
                         <span className="inline-flex items-center rounded-sm bg-primary/95 px-3 py-1 text-xs font-bold text-primary-foreground shadow-lg backdrop-blur-sm uppercase tracking-widest">
                            {project.category}
                         </span>
                      </div>
                    </div>

                    {}
                    <div className="flex flex-col p-6 md:p-10 bg-background">
                      <DialogHeader className="mb-6 text-left">
                        <div className="mb-2 flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-4 w-4 text-primary" />
                          <span className="text-sm uppercase font-bold tracking-widest">
                            {project.location}
                          </span>
                        </div>
                        <DialogTitle className="font-sans text-3xl font-bold text-foreground md:text-4xl uppercase tracking-tighter">
                          {project.title}
                        </DialogTitle>
                        <DialogDescription className="mt-4 text-base leading-relaxed text-muted-foreground">
                          {project.description}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="mt-auto space-y-8">
                        {}
                        <div className="grid grid-cols-2 gap-4 border-y border-border py-6">
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                              <Ruler className="h-3 w-3 text-primary" /> Substrate
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {project.specs.area}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                              <SafeHydrate><Calendar className="h-3 w-3 text-primary" /></SafeHydrate> Completed
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {project.specs.year}
                            </span>
                          </div>
                          <div className="col-span-2 flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                              <BrickWall className="h-3 w-3 text-primary" /> Sealant Products Used
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {project.materials.join(", ")}
                            </span>
                          </div>
                        </div>

                        {}
                        <div className="flex justify-end gap-3">
                          <DialogClose asChild>
                            <Button variant="outline" className="rounded-md uppercase font-bold tracking-widest text-xs">Close</Button>
                          </DialogClose>
                          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md uppercase font-bold tracking-widest text-xs shadow-md">
                            Inquire About This Service
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}